import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import ComicCard from '@/components/feature/ComicCard';
import { getComicBySlug, getRelatedComics, getCategoryName } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [comic, setComic] = useState<Comic | null>(null);
  const [related, setRelated] = useState<Comic[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
    if (slug) {
      const found = getComicBySlug(slug);
      setComic(found || null);
      if (found) {
        setRelated(getRelatedComics(found, 4));
      }
    }
  }, [slug]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mt-4 text-lg">找不到该漫画</p>
            <Link
              to="/category/all"
              className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const primaryCategory = comic.categories[0];
  const categoryName = getCategoryName(primaryCategory);
  const canonicalUrl = typeof window !== 'undefined' ? `${window.location.origin}/comic/${comic.slug}` : '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫之家`,
    description: comic.description,
    image: comic.cover,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫之家',
    },
  };

  return (
    <>
      <SEO
        title={`${comic.title}_${comic.author}_韩漫在线免费阅读_韩漫之家`}
        description={`${comic.title}，作者${comic.author}。${comic.description.substring(0, 100)}... 韩漫之家提供无删减下拉式免费在线阅读。`}
        keywords={`${comic.title}, ${comic.author}, ${comic.tags.join(', ')}, 韩漫在线, 韩漫之家`}
        canonicalUrl={canonicalUrl}
        ogImage={comic.cover}
        ogType="article"
        schemaJson={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.category'), href: '/category/all' },
                { label: categoryName, href: `/category/${primaryCategory}` },
                { label: comic.title },
              ]}
            />

            {/* Comic Detail */}
            <div className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[2/3] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    title={comic.title}
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">
                    <strong>{t('comic.author')}：</strong>{comic.author}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </div>
                    <span className="font-semibold text-accent-600">{comic.rating}</span>
                    <span className="text-foreground-500">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1 text-foreground-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-sm"></i>
                    </div>
                    <span>{(comic.views / 10000).toFixed(1)}万 {t('comic.views')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-foreground-500">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-2-line text-sm"></i>
                    </div>
                    <span>共 {comic.chapters.length} 话</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <Link
                  to={`/comic/${comic.slug}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
                >
                  {t('comic.read')}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>

            {/* Chapters */}
            <div className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                {t('comic.chapters')}（共 {comic.chapters.length} 话）
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-background-200/70">
                  {comic.chapters.map((chapter) => (
                    <a
                      key={chapter.id}
                      href={`#chapter-${chapter.number}`}
                      className="block p-3 bg-background-50 hover:bg-primary-50 transition-colors duration-200 text-sm text-foreground-700 hover:text-primary-600"
                    >
                      <span className="font-medium">{chapter.title}</span>
                      <span className="block text-xs text-foreground-500 mt-1">{chapter.createdAt}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Related */}
            {related.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                  {t('comic.related')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6" data-product-shop>
                  {related.map((r) => (
                    <ComicCard key={r.id} comic={r} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}